import { useMemo, useState } from 'react'
import { Form, FormItem } from '@frontend/design_system'
import { useParams } from 'react-router-dom'
import { useAppSelector } from 'redux/hooks'
import { FormValues, VehicleDetailsFormProps } from './types'
import { CAR_TABS, VEHICLE_DETAILS_FORM_VALUES } from 'texts/vehicleDetails'
import { DriverAgeRanges } from 'ui/components/DriverAgeRanges'
import { useDriverAgeRangeValidation } from 'ui/components/DriverAgeRanges/hooks/useDriverAgeRangeValidation'
import { LeftSide } from './components/LeftSide/LeftSide'
import {
  isMigratedUserFlowSelector,
  isOperatorSelector,
  userDataSelector,
} from 'redux/login/selectors'
import { ACCOUNT_STATUSES, REGISTRATION_STATUSES } from 'api/auth/constants'
import { VehicleInformationForm } from './components/VehicleInformationForm/VehicleInformationForm'
import { initialFormValues } from './constants'
import { RightSide } from './components/RightSide/RightSide'
import { VehiclesTable } from '../VehiclesTable'
import { replaceNotAvailable, replaceShortNotAvailable } from './utils'
import { FormChildren, FormItemChildren, ValidationType } from 'types/form'
import { MultiselectItem } from 'types/multiselect'
import { scrollTop } from 'utils/scrollTop'
import { ApprovalInfoBadges } from 'ui/components/ApprovalInfoBadges'
import { VEHICLE_DETAILS_FORM_ID } from '../../constants'
import { UnsavedChangesModal } from 'ui/components/UnsavedChangesModal'
import styles from './styles.module.scss'
import { MigratedUserBadge } from 'ui/components/MigratedUserBadge'

export const VehicleDetailsForm = ({
  handleSubmit,
  formValues,
  isEditing,
  isLoading,
  vehicleDetailsData,
}: VehicleDetailsFormProps) => {
  const {
    carCategoryId,
    companyClass,
    sippCode,
    car,
    hirePoints,
    airConditioning,
    transmission,
    fuel,
    sits,
    doors,
    driverAgeRange,
    carType,
    carModelName,
    quantity,
  } = initialFormValues
  const { id } = useParams()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [driverAgeError, setDriverAgeError] = useState('')
  const [activeKey, setActiveKey] = useState(CAR_TABS.DETAILS)
  const [sipp, setSipp] = useState('')
  const [base64Image, setBase64Image] = useState('')
  const { accountStatus, sectionsStatuses } = useAppSelector(userDataSelector)
  const isOperator = useAppSelector(isOperatorSelector)
  const showLimitInfoAndTable =
    !isOperator && accountStatus !== ACCOUNT_STATUSES.APPROVED
  const isPendingUser =
    !isOperator &&
    sectionsStatuses?.carsStatus !== REGISTRATION_STATUSES.APPROVED
  const isMigratedUserFlow = useAppSelector(isMigratedUserFlowSelector)

  const checkDriverAgeRange = useDriverAgeRangeValidation(false)

  const onSubmit = (data: Partial<FormValues>, validate: boolean) => {
    setIsSubmitting(false)
    let driverAgeError
    const isFilledFieldExist = data.driverAgeRange?.items.some(
      ({ ageStart, ageEnd, price, maxPrice }) =>
        ageStart || ageEnd || price || maxPrice
    )
    if (isFilledFieldExist) {
      driverAgeError = checkDriverAgeRange(data.driverAgeRange)
      if (driverAgeError) {
        setDriverAgeError(driverAgeError)
        scrollTop()
      }
    }

    const isNewCar = !vehicleDetailsData.cars.some((el) => el.id === data.car)

    if (validate && !driverAgeError) {
      setIsSubmitting(true)
      handleSubmit({
        ...data,
        driverAgeRange: isFilledFieldExist ? data.driverAgeRange : undefined,
        carCategoryId: Number(data.carCategoryId),
        carId: !isNewCar ? Number(data.car) : null,
        hirePoints: data.hirePoints?.map((el) => Number(el?.value)),
        airConditioning: replaceShortNotAvailable(data.airConditioning),
        fuel: replaceShortNotAvailable(data.fuel),
        transmission: replaceShortNotAvailable(data.transmission),
        carModelName: isNewCar ? data.car : undefined,
        carModelImageContent: isNewCar ? base64Image : undefined,
        sippCode: sipp || data.sippCode,
        quantity: Number(data.quantity),
      })
    }
  }

  const initValues = useMemo(() => {
    const initData = {
      carCategoryId: formValues?.carCategoryId
        ? String(formValues?.carCategoryId)
        : carCategoryId,
      companyClass: formValues?.companyClass || companyClass,
      sippCode: formValues?.sippCode || sippCode,
      car:
        vehicleDetailsData.cars.find(
          (el) => Number(el.id) === Number(formValues?.carId)
        )?.id || car,
      hirePoints:
        formValues?.hirePoints?.reduce((acc, el) => {
          const location = vehicleDetailsData.locations.find(
            (location) => Number(location.id) === Number(el)
          )
          if (location) {
            acc.push({ label: location.name, value: location.id })
          }
          return acc
        }, [] as MultiselectItem[]) || hirePoints,
      airConditioning:
        replaceNotAvailable(formValues?.airConditioning) || airConditioning,
      transmission:
        replaceNotAvailable(formValues?.transmission) || transmission,
      fuel: replaceNotAvailable(formValues?.fuel) || fuel,
      sits: replaceShortNotAvailable(formValues?.sits) || sits,
      doors: replaceShortNotAvailable(formValues?.doors) || doors,
      driverAgeRange: formValues?.driverAgeRange || driverAgeRange,
      carType: formValues?.carType || carType,
      carModelName,
      quantity: formValues?.quantity ? String(formValues?.quantity) : quantity,
    }
    return initData
  }, [formValues, vehicleDetailsData])

  const carTabs = (checkValidation: () => ValidationType) => [
    {
      tab: CAR_TABS.DETAILS,
      key: CAR_TABS.DETAILS,
      children: (
        <VehicleInformationForm
          vehicleDetailsData={vehicleDetailsData}
          changeActiveKey={setActiveKey}
          checkValidation={checkValidation}
        />
      ),
    },
    {
      tab: CAR_TABS.DRIVER_AGE_RANGE,
      key: CAR_TABS.DRIVER_AGE_RANGE,
      children: (
        <FormItem id={VEHICLE_DETAILS_FORM_VALUES.DRIVER_AGE_RANGE.ID}>
          {({ onChange, value }: FormItemChildren) => (
            <DriverAgeRanges
              driverAgeRangeData={value}
              error={driverAgeError}
              onDriverAgeRangeDataChange={(newValue) => {
                setDriverAgeError('')
                onChange(newValue)
              }}
            />
          )}
        </FormItem>
      ),
    },
  ]

  return (
    <>
      <Form
        initValues={initValues}
        onSubmit={onSubmit}
        className={styles.form}
        reduxId={VEHICLE_DETAILS_FORM_ID}
        formId={VEHICLE_DETAILS_FORM_ID}
        isMountValidate={isEditing && isMigratedUserFlow}
      >
        {({ values, checkValidation, isDirty, errors }: FormChildren) => (
          <>
            {isPendingUser && (
              <ApprovalInfoBadges
                isRequested={
                  sectionsStatuses?.carsStatus ===
                  REGISTRATION_STATUSES.REQUESTED
                }
              />
            )}
            <MigratedUserBadge errors={errors} />
            <UnsavedChangesModal isDirty={isDirty && !isSubmitting} />
            <div className={styles.container}>
              <LeftSide
                changeActiveKey={setActiveKey}
                activeKey={activeKey}
                tabs={carTabs(checkValidation)}
                driverAgeError={driverAgeError}
                isEditing={!!isEditing}
              />
              <RightSide
                activeKey={activeKey}
                formValues={values}
                setBase64Image={setBase64Image}
                isLoading={isLoading}
                sipp={sipp}
                handleChangeSipp={setSipp}
                isEditing={isEditing}
                isDirty={isDirty}
                vehicleDetailsData={vehicleDetailsData}
              />
            </div>
          </>
        )}
      </Form>
      {showLimitInfoAndTable && <VehiclesTable currentId={id} />}
    </>
  )
}
